import React, {ReactNode, useEffect, useRef, useState} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {KBTreeIconPicker} from './KBTreeCategoryItem/KBTreeIconPicker';

export const FALLBACK_ICON = 'far fa-folder';

interface KBTreeEditItemStructureProps {
  disabled?: boolean;
  onSubmit: () => Promise<unknown>;
  onCancel: () => void;
  children: (value: boolean) => ReactNode;
  className?: string;
  inPopover?: boolean;
}
export function KBTreeEditItemStructure({
  disabled,
  children,
  onSubmit,
  onCancel,
  className,
  inPopover = false,
}: KBTreeEditItemStructureProps) {
  const [saving, setSaving] = useState<boolean>(false);
  const isMounted = useRef(true);
  const disableSaving = () => {
    if (isMounted.current) {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onSave: React.EventHandler<
    React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  > = (e) => {
    e.preventDefault();
    setSaving(true);
    return onSubmit().then(disableSaving, disableSaving);
  };

  return (
    <StyledForm className={cs('input-group', className)} onSubmit={onSave}>
      {children(saving)}
      <div className={inPopover ? 'col-12 text-right' : 'input-group-append'}>
        <button
          className="btn btn-outline-secondary"
          disabled={saving}
          type="button"
          onClick={onCancel}
        >
          <i className="fa fa-fw fa-times" />
        </button>
        <button
          type="button"
          onClick={onSave}
          className={cs('btn btn-outline-primary', {'ml-1': inPopover})}
          disabled={disabled}
        >
          <i
            className={`fa fa-fw ${
              saving ? 'fa-circle-notch fa-spin' : 'fa-check'
            }`}
          />
        </button>
      </div>
    </StyledForm>
  );
}

interface KBTreeEditItemNameProps {
  inputClass?: string;
  initialIcon?: string;
  initialValue?: string;
  placeholder: string;
  onSubmit: (value: string, icon: string) => Promise<unknown>;
  onCancel: () => any;
}
export function KBTreeEditItemName({
  inputClass,
  onSubmit,
  onCancel,
  placeholder,
  initialValue = '',
  initialIcon,
}: KBTreeEditItemNameProps) {
  const [value, setValue] = useState<string>(initialValue);
  const [icon, setIcon] = useState<string>(initialIcon || FALLBACK_ICON);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current != null && inputRef.current.value !== '') {
      inputRef.current.select();
    }
  }, []);

  return (
    <KBTreeEditItemStructure
      onSubmit={() => onSubmit(value, icon !== FALLBACK_ICON ? icon : '')}
      onCancel={onCancel}
    >
      {(saving) => (
        <>
          <KBTreeIconPicker disabled={saving} icon={icon} onChange={setIcon} />
          <input
            ref={inputRef}
            type="text"
            autoFocus={true}
            placeholder={placeholder}
            className={cs('form-control', inputClass)}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            readOnly={saving}
          />
        </>
      )}
    </KBTreeEditItemStructure>
  );
}

const StyledForm = styled.form`
  @media (max-width: 575px) {
    .input-group-append {
      margin-left: 0;
      width: 100%;
      display: flex;
      > * {
        flex-basis: 50%;

        &:first-child {
          border-radius: 0 0 0 0.25rem;
        }
        &:last-child {
          border-radius: 0 0 0.25rem 0;
        }
      }
    }
  }
`;
